import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { userService } from '../services/user.service';
import {
  UserCheck,
  UserX,
  Mail,
  Phone,
  Calendar,
  AlertCircle,
  CheckCircle,
  XCircle,
  RefreshCw,
  ArrowLeft
} from 'lucide-react';
import ConfirmModal from '../components/ConfirmModal';

const PendingUsers = () => {
  const navigate = useNavigate();
  const { user, isAdmin } = useAuth();
  const [pendingUsers, setPendingUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    type: null,
    userId: null,
  });

  useEffect(() => {
    if (user && isAdmin()) {
      loadPendingUsers();
    }
  }, [user, isAdmin]);

  const loadPendingUsers = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await userService.getPendingUsers();
      setPendingUsers(response);
    } catch (err) {
      console.error('Erreur lors du chargement des utilisateurs en attente:', err);
      setError('Impossible de charger la liste des utilisateurs en attente.');
    } finally {
      setLoading(false);
    }
  };

  const handleActivate = (userId) => {
    setConfirmModal({
      isOpen: true,
      type: 'activate',
      userId,
    });
  };

  const handleReject = (userId) => {
    setConfirmModal({
      isOpen: true,
      type: 'reject',
      userId,
    });
  };

  const handleConfirmAction = async () => {
    const { type, userId } = confirmModal;

    try {
      setActionLoading(userId);
      setError('');
      setSuccess('');

      if (type === 'activate') {
        await userService.activateUser(userId);
        setSuccess('Utilisateur activé avec succès ! Un email de confirmation lui a été envoyé.');
      } else if (type === 'reject') {
        await userService.suspendUser(userId);
        setSuccess('Utilisateur suspendu avec succès.');
      }

      // Recharger la liste
      await loadPendingUsers();
      setConfirmModal({ isOpen: false, type: null, userId: null });
    } catch (err) {
      console.error('Erreur lors de l\'action:', err);
      setError(err.message || 'Impossible d\'effectuer cette action.');
    } finally {
      setActionLoading(null);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!isAdmin()) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-xl shadow-lg p-8 text-center border border-gray-200">
          <div className="w-16 h-16 bg-red-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <XCircle className="w-10 h-10 text-red-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Accès refusé</h2>
          <p className="text-gray-600">
            Vous n'avez pas les permissions nécessaires pour accéder à cette page.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6">
      <div className="max-w-6xl mx-auto">
        {/* Bouton retour */}
        <div className="mb-4">
          <button
            onClick={() => navigate('/admin')}
            className="inline-flex items-center gap-2 text-primary-600 hover:text-primary-700 font-medium transition-colors group"
          >
            <ArrowLeft size={20} className="group-hover:-translate-x-1 transition-transform" />
            <span>Retour à l'administration</span>
          </button>
        </div>

        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900">
              Utilisateurs en attente
            </h1>
            <button
              onClick={loadPendingUsers}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              <span className="hidden sm:inline">Actualiser</span>
            </button>
          </div>
          <p className="text-gray-600">
            Validez les nouveaux comptes utilisateurs pour leur permettre de se connecter.
          </p>
        </div>

        {/* Messages de succès/erreur */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        {success && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-green-800">{success}</p>
          </div>
        )}

        {/* Loading */}
        {loading && (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
          </div>
        )}

        {/* Liste vide */}
        {!loading && pendingUsers.length === 0 && (
          <div className="bg-white rounded-xl shadow-sm p-12 text-center border border-gray-200">
            <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <UserCheck className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Aucun utilisateur en attente
            </h3>
            <p className="text-gray-600">
              Tous les comptes utilisateurs ont été traités.
            </p>
          </div>
        )}

        {/* Liste des utilisateurs */}
        {!loading && pendingUsers.length > 0 && (
          <div className="space-y-4">
            {pendingUsers.map((pendingUser) => (
              <div
                key={pendingUser.id}
                className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 hover:shadow-md transition-shadow"
              >
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  {/* Informations utilisateur */}
                  <div className="flex-1 space-y-3">
                    <div className="flex items-start gap-3">
                      <div className="w-12 h-12 bg-yellow-100 text-yellow-700 rounded-full flex items-center justify-center flex-shrink-0 font-bold text-lg">
                        {pendingUser.firstName?.[0]}{pendingUser.lastName?.[0]}
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900">
                          {pendingUser.firstName} {pendingUser.lastName}
                        </h3>
                        <div className="flex items-center gap-2 text-sm text-gray-600 mt-1">
                          <span className="px-2 py-1 bg-yellow-100 text-yellow-700 rounded-md text-xs font-medium">
                            EN ATTENTE
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                      <div className="flex items-center gap-2 text-gray-600">
                        <Mail className="w-4 h-4 text-gray-400" />
                        <span>{pendingUser.email}</span>
                      </div>
                      {pendingUser.phoneNumber && (
                        <div className="flex items-center gap-2 text-gray-600">
                          <Phone className="w-4 h-4 text-gray-400" />
                          <span>{pendingUser.phoneNumber}</span>
                        </div>
                      )}
                      <div className="flex items-center gap-2 text-gray-600">
                        <Calendar className="w-4 h-4 text-gray-400" />
                        <span className="text-xs">
                          Inscrit le {formatDate(pendingUser.createdAt)}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex flex-row md:flex-col gap-2 md:min-w-[140px]">
                    <button
                      onClick={() => handleActivate(pendingUser.id)}
                      disabled={actionLoading === pendingUser.id}
                      className="flex-1 md:w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium text-sm"
                    >
                      {actionLoading === pendingUser.id ? (
                        <>
                          <RefreshCw className="w-4 h-4 animate-spin" />
                          <span>Activation...</span>
                        </>
                      ) : (
                        <>
                          <UserCheck className="w-4 h-4" />
                          <span>Valider</span>
                        </>
                      )}
                    </button>
                    <button
                      onClick={() => handleReject(pendingUser.id)}
                      disabled={actionLoading === pendingUser.id}
                      className="flex-1 md:w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium text-sm"
                    >
                      <UserX className="w-4 h-4" />
                      <span>Refuser</span>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modal de confirmation */}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal({ isOpen: false, type: null, userId: null })}
        onConfirm={handleConfirmAction}
        title={confirmModal.type === 'activate' ? 'Activer le compte' : 'Suspendre le compte'}
        message={
          confirmModal.type === 'activate'
            ? 'Voulez-vous vraiment activer ce compte utilisateur ? Un email de confirmation sera envoyé.'
            : 'Voulez-vous vraiment suspendre ce compte utilisateur ? L\'utilisateur ne pourra plus se connecter.'
        }
        confirmText={confirmModal.type === 'activate' ? 'Activer' : 'Suspendre'}
        cancelText="Annuler"
        variant={confirmModal.type === 'activate' ? 'success' : 'danger'}
        loading={actionLoading === confirmModal.userId}
      />
    </div>
  );
};

export default PendingUsers;
